// ═════════════════════════════════════════════════════════════════════════════
//  MY ACTIONS — corrective actions assigned to the signed-in user (Fase 2 CA4)
//  Field-app counterpart of the admin Actions tab (js/admin/93-corrective.js).
//  RLS scopes reads to assigned_to = auth.uid() for non-admins; the UI only
//  exposes closing with a note. Online-only: no offline queue — on fetch
//  failure the page says so and offers retry.
// ═════════════════════════════════════════════════════════════════════════════

let _myActionCloseId = null;

// Priority dot fill — a small solid circle, the only visual urgency cue
// (the text label is the priority word itself via the title tooltip, not
// practical on touch). Dark keeps the original v485 literals; light swaps to
// darker AA-safe shades so the dot doesn't wash out on the new white card.
// A plain hex has no [data-theme] cascade to pin against, so this is read
// fresh on every render (not a module-load-time const) — otherwise a user
// who opens My Actions after toggling theme (no reload) would still see
// whatever shade happened to be baked in when the script first parsed.
// Priority colour used to be picked here in JS, per theme, as six literal
// hexes. It is a CSS concern and it is now one: .fp-action-card--high/medium/low
// in the token layer paint the dot, so the palette lives in one file.

function _myActionsEsc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function _myActionDaysOverdue(row) {
  if (row.status !== 'open') return 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(row.due_date + 'T00:00:00');
  const days = Math.round((today - due) / 86_400_000);
  return days > 0 ? days : 0;
}

// Dashboard badge — open-count for the signed-in user. Called on every
// dashboard show; cheap HEAD count. Returns the count so callers (the Today
// board) can reuse the same fetch instead of firing a second identical query.
async function refreshMyActionsBadge() {
  const badge = document.getElementById('myActionsBadge');
  if (!window.currentUser) return null;
  try {
    const { count } = await sbClient.from('corrective_actions')
      .select('id', { count: 'exact', head: true })
      .eq('assigned_to', window.currentUser.id).eq('status', 'open');
    if (badge) { badge.textContent = count || 0; badge.hidden = !count; }
    return count || 0;
  } catch { return null; /* badge (if present) just stays as-is offline */ }
}

function openMyActionsPage() {
  showInnerPage('myActionsPage');
  loadMyActions();
}

async function loadMyActions() {
  const wrap = document.getElementById('myActionsList');
  if (!wrap || !window.currentUser) return;
  wrap.innerHTML = '<div class="docs-loading">Loading…</div>';

  const { data, error } = await sbClient.from('corrective_actions')
    .select('id, description, priority, due_date, status, closure_note, closed_at, source_label, projects(name)')
    .eq('assigned_to', window.currentUser.id)
    .order('due_date', { ascending: true })
    .limit(50);

  if (error) {
    wrap.innerHTML = `
      <div class="fx-empty">
        <div class="fx-empty-t">Couldn't load your actions — you may be offline.</div>
        <button class="fx-retry" onclick="loadMyActions()">Retry</button>
      </div>`;
    return;
  }

  const open = (data || []).filter(r => r.status === 'open')
    .sort((a, b) => _myActionDaysOverdue(b) - _myActionDaysOverdue(a));
  const closed = (data || []).filter(r => r.status === 'closed')
    .sort((a, b) => new Date(b.closed_at) - new Date(a.closed_at));

  // Redesign v3: the card is the mock's — priority dot, the sentence at full
  // size, an overdue pill only when it IS overdue, the source line under it,
  // and one button. The old inline styles carried the dark palette as literals
  // and won over every stylesheet.
  const card = (r) => {
    const days = _myActionDaysOverdue(r);
    const overdue = days > 0
      ? `<span class="fp-overdue-pill">Overdue ${days}d</span>`
      : '';
    // "Due Aug 10", not "Due 2026-08-11": the ISO string is a database format,
    // and this line is read at arm's length on a phone.
    const due = r.due_date
      ? new Date(r.due_date + 'T12:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
      : '';
    const meta = [
      r.projects?.name,
      due ? `Due ${due}` : '',
      r.source_label,
    ].filter(Boolean).map(_myActionsEsc).join(' · ');
    const closedLine = r.status === 'closed'
      ? `<div class="fp-closed-line"><i data-lucide="check"></i>Closed — ${_myActionsEsc(r.closure_note || '')}</div>`
      : '';
    const btn = r.status === 'open'
      ? `<button class="fx-actbtn" onclick="openMyActionCloseModal('${r.id}')">Close with note</button>`
      : '';
    return `
    <div class="fp-action-card fp-action-card--${_myActionsEsc(r.priority || 'medium')}${days > 0 ? ' fp-action-card--overdue' : ''}">
      <div class="fp-action-head">
        <span class="fp-action-pr" title="${_myActionsEsc(r.priority)}" aria-label="${_myActionsEsc(r.priority)} priority"></span>
        <div class="fp-action-desc">${_myActionsEsc(r.description)}</div>
        ${overdue}
      </div>
      <div class="fp-action-meta">${meta}</div>
      ${closedLine}
      ${btn}
    </div>`;
  };

  const openHtml = open.length
    ? open.map(card).join('')
    : `<div class="fx-empty">
         <span class="fp-empty-ic fp-empty-ic--ok" aria-hidden="true"><i data-lucide="circle-check-big"></i></span>
         <div class="fx-empty-t">Nothing open</div>
         <div class="fx-empty-s">You're all caught up.</div>
       </div>`;
  const closedHtml = closed.length
    ? `<details class="fx-more">
         <summary>Closed (${closed.length})</summary>
         <div class="fx-more-body">${closed.map(card).join('')}</div>
       </details>`
    : '';
  wrap.innerHTML = openHtml + closedHtml;
  if (window.lucide) lucide.createIcons();
}

// ── Close-with-note modal ─────────────────────────────────────────────────────
function openMyActionCloseModal(id) {
  _myActionCloseId = id;
  const note = document.getElementById('myActionCloseNote');
  const err = document.getElementById('myActionCloseError');
  if (note) note.value = '';
  if (err) err.textContent = '';
  document.getElementById('myActionCloseModal').style.display = 'flex';
  if (note) note.focus();
}

function closeMyActionCloseModal() {
  document.getElementById('myActionCloseModal').style.display = 'none';
  _myActionCloseId = null;
}

async function submitMyActionClose() {
  const err = document.getElementById('myActionCloseError');
  const note = (document.getElementById('myActionCloseNote')?.value || '').trim();
  if (err) err.textContent = '';
  if (!note) { if (err) err.textContent = 'Describe what was done — a note is required.'; return; }

  const { error } = await sbClient.from('corrective_actions').update({
    status: 'closed', closed_at: new Date().toISOString(),
    closed_by: window.currentUser?.id || null, closure_note: note,
  }).eq('id', _myActionCloseId);
  if (error) {
    if (err) err.textContent = error.code === '23514' ? 'A closure note is required.' : 'Could not close — check your connection and try again.';
    return;
  }
  closeMyActionCloseModal();
  if (typeof showToast === 'function') showToast('Action closed', 'success');
  loadMyActions();
  refreshMyActionsBadge();
}
